import { Directive } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractLandingPageContentComponent } from '../../pages/landing/landing-page.component';
import { TextSectionTextTransformer } from '../textsection/rich-text-sections/base-text-section.component';
import { ReplaceTokenisedParametersTextTransformer } from '../textsection/rich-text-sections/text-section-model-transformers';

export type TemplateParameters = { [key: string]: string };
export type DescribedTemplateParameter = { key: string, value: string, description: string };

@Directive()
export abstract class BaseEmailTemplatesComponent extends AbstractLandingPageContentComponent {

  constructor() {
    super();
  }

  private _textTransformer: TextSectionTextTransformer;
  get textTransformer(): TextSectionTextTransformer {
    if (this._textTransformer == null) {
      if (this._replacementParameters != null) {
        this._textTransformer = new ReplaceTokenisedParametersTextTransformer(() => this._replacementParameters || {});
      }
    }
    return this._textTransformer;
  }

  private _imageAssetFilenames: string[]
  get imageAssetFilenames(): string[] {
    if (this._imageAssetFilenames == null) {
      this._imageAssetFilenames = this.getImageAssetFilenames() || [];
    }
    return this._imageAssetFilenames;
  }

  private _replacementParameters: TemplateParameters;
  private _replacementParameterPairs: DescribedTemplateParameter[];
  get replacementParameters(): DescribedTemplateParameter[] {
    if (this._replacementParameterPairs == null) {
      const replacementParams: TemplateParameters = this._replacementParameters;
      if (replacementParams) {
        this._replacementParameterPairs = transformEmailTemplateParametersToDescribedParameters(replacementParams, this.imageAssetFilenames, (key) => this.getReplacementDescription(key), this.textTransformer);
      }
    }
    return this._replacementParameterPairs;
  }


  protected getImageAssetFilenames(): string[] {
    return getEmailTemplateDefaultImageAssetFilenames();
  }

  protected getReplacementDescription(key: string) {
    return getEmailTemplateReplacementDescription(key, this.imageAssetFilenames, (key) => this.getImageDescription(key));
  }

  getImageDescription(imageFilename): string {
    return getEmailTemplateImageDescription(imageFilename);
  }


  ngOnInit(): void {
    super.ngOnInit();
    this.loadExampleTemplateParameters();
  }

  protected loadExampleTemplateParameters(backgroundServiceCall: boolean = false) {
    this._replacementParameters = null;
    this._replacementParameterPairs = null;
    this.getExampleTemplateParameters().subscribe(replacementParams => {
      addEmailTemplateStandardReplacementParameters(replacementParams);
      this._replacementParameters = replacementParams;
      this._replacementParameterPairs = null;
    });
  }

  protected abstract getExampleTemplateParameters(backgroundServiceCall?: boolean): Observable<TemplateParameters>;

}

export function addEmailTemplateStandardReplacementParameters(replacementParams: TemplateParameters) {
  if (replacementParams) {
    replacementParams['image|imageUrl'] = '<img src="%{imageUrl}"/>';
  }
}

export function transformEmailTemplateParametersToDescribedParameters(replacementParams: TemplateParameters, imageAssetFilenames: string[] = [], replacementDescription: (key: string) => string, textTransformer: TextSectionTextTransformer): DescribedTemplateParameter[] {
  const pairs = [];
  for (let key of Object.keys(replacementParams)) {
    pairs.push({ key: key, value: replacementParams[key], description: replacementDescription(key) });
  }

  for (let imageFilename of imageAssetFilenames) {
    const exampleToken = "image|%{systemUrlBase}/assets/" + imageFilename;
    const exampleValue = textTransformer.transformText('%{' + exampleToken + '}')
    pairs.push({ key: exampleToken, value: exampleValue, description: replacementDescription(exampleToken) });
  }
  return pairs;
}

export function getEmailTemplateImageDescription(imageFilename: string): string {
  if (imageFilename == "FE_Primary_Logo_Green_small.png") {
    return "Linked Primary Logo (green/small)"
  }
  return null;
}

export function getEmailTemplateDefaultImageAssetFilenames(): string[] {
  return ["FE_Primary_Logo_Green_small.png"];
}

export function getEmailTemplateReplacementDescription(key: string, imageAssetFilenames: string[] = [], imageDescriptionFn: (imageFilename) => string = getEmailTemplateImageDescription): string {
  if (key == 'systemUrlBase') {
    return 'System URL';
  }
  if (key == 'username') {
    return 'Username/Email';
  }
  if (key == 'jwt') {
    return 'Link Token';
  }
  if (key == 'additionalText') {
    return 'Additional Comments';
  }
  if (key == 'expiryMinutes') {
    return 'Expiry Minutes';
  }
  if (key == 'expiryHours') {
    return 'Expiry Hours';
  }
  if (key == 'expiryDays') {
    return 'Expiry Days';
  }
  if (key == 'expiryTimeText') {
    return 'Expiry Time With Unit';
  }
  if (key == 'image|imageUrl') {
    return 'Linked Image (for use when embedded images display oddly in Outlook Desktop application)';
  }
  for (let imageFilename of imageAssetFilenames) {
    const exampleToken = "image|%{systemUrlBase}/assets/" + imageFilename;
    if (key == exampleToken) {
      return imageDescriptionFn && imageDescriptionFn(imageFilename) || "";
    }
  }
  return null;
}
